import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  PieChart,
  BarChart3,
  TrendingUp,
  DollarSign,
  Calendar,
  Download,
  Target,
  ArrowLeft,
  FileText,
  Shield,
} from 'lucide-react';
import { LogoIcon } from '../components/Logo';
import { 
  ResponsiveContainer, 
  PieChart as RechartsPie, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Legend 
} from 'recharts';
import PortfolioRiskIntelligence from '../components/dashboard/PortfolioRiskIntelligence';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PHASE_COLORS = {
  define: '#3B82F6',
  measure: '#8B5CF6',
  analyze: '#F59E0B',
  improve: '#22C55E',
  control: '#06B6D4',
  closed: '#64748B',
};

const PHASE_ORDER = ['define', 'measure', 'analyze', 'improve', 'control', 'closed'];

export default function PortfolioReporting() {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('all');
  const [reportData, setReportData] = useState({
    phaseDistribution: [],
    statusSummary: [],
    projectTimeline: [],
    financialImpact: { totalSavings: 0, avgROI: 0, projectedAnnual: 0 },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPortfolioData();
  }, [timeRange]);

  const fetchPortfolioData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const projectsData = await response.json();
        setProjects(projectsData);
        calculateReportData(projectsData);
      }
    } catch (error) {
      toast.error('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  const calculateReportData = (projectsData) => {
    // Filter by time range
    let filteredProjects = projectsData;
    const now = new Date();
    
    if (timeRange === '30d') {
      const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
      filteredProjects = projectsData.filter(p => new Date(p.created_at) >= thirtyDaysAgo);
    } else if (timeRange === '90d') {
      const ninetyDaysAgo = new Date(now - 90 * 24 * 60 * 60 * 1000);
      filteredProjects = projectsData.filter(p => new Date(p.created_at) >= ninetyDaysAgo);
    } else if (timeRange === '1y') {
      const oneYearAgo = new Date(now - 365 * 24 * 60 * 60 * 1000);
      filteredProjects = projectsData.filter(p => new Date(p.created_at) >= oneYearAgo);
    }

    // Phase distribution for pie chart
    const phaseCounts = {};
    filteredProjects.forEach(p => {
      phaseCounts[p.phase] = (phaseCounts[p.phase] || 0) + 1;
    });
    const phaseDistribution = Object.entries(phaseCounts).map(([phase, count]) => ({
      name: phase.charAt(0).toUpperCase() + phase.slice(1),
      value: count,
      color: PHASE_COLORS[phase],
    }));

    // Status summary for bar chart
    const statusSummary = PHASE_ORDER.map(phase => ({
      phase: phase.charAt(0).toUpperCase() + phase.slice(1),
      count: phaseCounts[phase] || 0,
    }));

    // Simulated financial impact (in real app, this would come from project artifacts)
    const completedProjects = filteredProjects.filter(p => p.phase === 'closed' || p.status === 'closed');
    const totalSavings = completedProjects.length * 50000; // Simulated $50k per project
    const avgROI = completedProjects.length > 0 ? 150 : 0; // Simulated 150% ROI
    const projectedAnnual = totalSavings * 12 / Math.max(1, filteredProjects.length);

    setReportData({
      phaseDistribution,
      statusSummary,
      projectTimeline: filteredProjects.slice(0, 10), // Last 10 projects
      financialImpact: { totalSavings, avgROI, projectedAnnual },
    });
  };

  const exportReport = () => {
    // Create CSV export
    const headers = ['Project', 'Phase', 'Status', 'Industry', 'Created'];
    const rows = projects.map(p => [
      p.title,
      p.phase,
      p.status,
      p.industry || 'N/A',
      new Date(p.created_at).toLocaleDateString(),
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portfolio-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Report exported successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="portfolio-reporting">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <LogoIcon className="w-10 h-10" />
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <PieChart className="w-6 h-6 text-cyan-400" />
              Portfolio Reporting
            </h1>
            <p className="text-slate-400 mt-1">
              Comprehensive view of your improvement portfolio
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Time range" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="1y">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={exportReport}
            className="bg-cyan-600 hover:bg-cyan-700"
            data-testid="export-report-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
          <Link to="/dashboard">
            <Button variant="outline" className="border-slate-600">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="risk-intelligence" className="space-y-6">
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="risk-intelligence" className="gap-2" data-testid="tab-risk-intelligence">
            <Shield className="w-4 h-4" />
            Risk Intelligence
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2" data-testid="tab-analytics">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Risk Intelligence Tab */}
        <TabsContent value="risk-intelligence">
          <PortfolioRiskIntelligence token={token} />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          {/* Financial Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-green-900/50 to-slate-800 border-green-700/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-300">Total Savings</p>
                <p className="text-3xl font-bold text-white">
                  ${reportData.financialImpact.totalSavings.toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-900/50 to-slate-800 border-purple-700/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-300">Average ROI</p>
                <p className="text-3xl font-bold text-white">
                  {reportData.financialImpact.avgROI}%
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-900/50 to-slate-800 border-cyan-700/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-cyan-300">Projected Annual</p>
                <p className="text-3xl font-bold text-white">
                  ${Math.round(reportData.financialImpact.projectedAnnual).toLocaleString()}
                </p>
              </div>
              <Calendar className="w-10 h-10 text-cyan-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Phase Distribution Pie Chart */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PieChart className="w-5 h-5 text-cyan-400" />
              Phase Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reportData.phaseDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPie>
                  <Pie
                    data={reportData.phaseDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {reportData.phaseDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1E293B', 
                      border: '1px solid #334155',
                      borderRadius: '8px'
                    }}
                  />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                No projects in selected time range
              </div>
            )}
          </CardContent>
        </Card>

        {/* Projects by Phase Bar Chart */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-400" />
              Projects by Phase
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={reportData.statusSummary}>
                <XAxis 
                  dataKey="phase" 
                  tick={{ fill: '#94A3B8', fontSize: 12 }}
                  axisLine={{ stroke: '#334155' }}
                />
                <YAxis 
                  tick={{ fill: '#94A3B8' }}
                  axisLine={{ stroke: '#334155' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1E293B', 
                    border: '1px solid #334155',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="count" fill="#22D3EE" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-400" />
            Recent Projects
          </CardTitle>
          <CardDescription className="text-slate-400">
            Latest projects in your portfolio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Project</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Phase</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Industry</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Created</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {reportData.projectTimeline.map((project) => (
                  <tr key={project.project_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="py-3 px-4">
                      <Link 
                        to={`/project/${project.project_id}`}
                        className="text-white hover:text-cyan-400"
                      >
                        {project.title}
                      </Link>
                    </td>
                    <td className="py-3 px-4">
                      <Badge 
                        style={{ backgroundColor: PHASE_COLORS[project.phase] }}
                        className="text-white capitalize"
                      >
                        {project.phase}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-slate-400">
                      {project.industry || 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-slate-400">
                      {new Date(project.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="text-slate-300 border-slate-600 capitalize">
                        {project.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
